import React, { useEffect, useRef, useState } from 'react';
import ASSET_PATHS from '../../../assets/config/assetPaths';
import { ImSpinner2 } from 'react-icons/im';
import { WorkOrderStatus } from "../../../types/workOrder";

interface PreviewImageUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    uploadedVideo: string[];
    openGallery: (event: React.ChangeEvent<HTMLInputElement> | Event, type: string) => void;
    workOrderDetail: any;
    workOrderId: string | undefined;
    setCurrentCapturedDetail: (object: any) => void;
}

const PreviewVideoUploadModal: React.FC<PreviewImageUploadModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    uploadedVideo,
    openGallery,
    workOrderDetail,
    workOrderId,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await onConfirm();
        } catch (error) {
            console.error('Error confirming action:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div
                ref={modalRef}
                className="w-[92%] max-w-md sm:max-w-lg md:max-w-xl bg-white rounded-lg shadow-xl p-3 sm:p-5 md:p-6 max-[350px]:p-2 max-[300px]:p-1 relative mx-4 max-[360px]:w-[96%] max-[350px]:mx-2 max-[300px]:mx-1"      >

                <button
                    className="absolute top-1 right-1 text-gray-500 hover:text-gray-700"
                    onClick={onClose}
                >
                    <img src={ASSET_PATHS.closeIcon} alt="Close Icon" />
                </button>

                <div
                    style={{
                        display: 'flex',
                        overflowX: uploadedVideo.length > 1 ? 'auto' : 'hidden',
                        gap: uploadedVideo.length > 1 ? '10px' : '0',
                        padding: uploadedVideo.length > 1 ? '10px' : '0',
                        whiteSpace: uploadedVideo.length > 1 ? 'nowrap' : 'normal',
                        scrollSnapType: uploadedVideo.length > 1 ? 'x mandatory' : 'none',
                        maxWidth: '100%',
                        justifyContent: uploadedVideo.length === 1 ? 'center' : 'start',
                    }}
                    className="flex justify-start items-center border border-gray-300 rounded-md mb-4 p-4"
                >
                    {uploadedVideo && uploadedVideo.length > 0 ? (
                        uploadedVideo.map((file, index) => (
                            <video
                                key={index}
                                src={file}
                                poster='/assets/images/poster.svg'
                                preload={isSafari ? 'none' : 'auto'}
                                controls
                                style={{
                                    flexShrink: 0,
                                    width: '300px',
                                    height: 'auto',
                                    maxHeight: 'calc(90vh - 200px)',
                                    boxSizing: 'border-box',
                                }}
                                className="rounded-md"
                            />
                        ))
                    ) : (
                        <p className="text-gray-500">Media preview</p>
                    )}
                </div>
                <div className="flex flex-row gap-4 max-[640px]:gap-3 max-[480px]:gap-2 max-[380px]:gap-1 max-[280px]:flex-col">
                    <div className="flex-1">
                        <label htmlFor="another-file-upload">
                            <button
                                className="w-full flex items-center justify-center gap-2 
                                border-2 border-blue-600 text-blue-600 py-2 rounded-md
                                text-sm sm:text-base max-[350px]:gap-1 min-[280px]:text-xs max-[300px]:text-xs"

                                onClick={handleButtonClick}
                            >
                                <img
                                    src={ASSET_PATHS.blueVideoIcon}
                                    alt="Camera"
                                    className="h-5 w-5"

                                />
                                <span className="whitespace-nowrap">
                                    Add Media
                                </span>
                            </button>
                        </label>
                        <input
                            ref={fileInputRef}
                            id={`another-file-upload`}
                            type="file"
                            accept="video/*, .mov"
                            multiple
                            className="hidden"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                openGallery(event, "video");
                            }}
                            disabled={workOrderDetail[+workOrderId!].workOrderStatus === WorkOrderStatus.Completed}
                        />
                    </div>

                    <button
                        className="flex-1 w-full bg-blue-600 text-white py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-60 text-sm sm:text-base max-[350px]:gap-1 min-[280px]:text-xs max-[300px]:text-xs"
                        onClick={handleConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? <ImSpinner2 className="animate-spin" /> : <>
                            <img
                                src={ASSET_PATHS.cloudUploadWhite}
                                alt="Camera"
                                className="h-5 w-5"

                            />
                            <span className="whitespace-nowrap">
                                Confirm Upload
                            </span>
                        </>}
                    </button>
                </div>

            </div>
        </div>
    );
};


export default PreviewVideoUploadModal;